import React, { useState } from 'react'
import Modal from '../Modal'
import { AutoColumn } from '../Column'
import styled from 'styled-components'
import { RowBetween } from '../Row'
import { TYPE, CloseIcon } from '../../theme'
import { ButtonError } from '../Button'
import { useRewardsV2Contract } from '../../hooks/useContract'
import { SubmittedView, LoadingView } from '../ModalViews'
import { TransactionResponse } from '@ethersproject/providers'
import { useTransactionAdder } from '../../state/transactions/hooks'
import FormattedCurrencyAmount from '../FormattedCurrencyAmount'
import { useActiveWeb3React } from '../../hooks'
import { LPRewardsV2Info, useServiceFee } from '../../state/rewardsV2/lpRewardsv2'
import { JSBI } from '@cronosdex/sdk'
import { SSRewardsV2Info } from '../../state/rewardsV2/ssRewardsv2'

const ContentWrapper = styled(AutoColumn)`
  width: 100%;
  padding: 1rem;
`

interface StakingModalProps {
  isOpen: boolean
  onDismiss: () => void
  stakingInfo: LPRewardsV2Info | SSRewardsV2Info
}

export default function BoosterWithdrawV2Modal({ isOpen, onDismiss, stakingInfo }: StakingModalProps) {
  const { account } = useActiveWeb3React()

  // monitor call to help UI loading state
  const addTransaction = useTransactionAdder()
  const [hash, setHash] = useState<string | undefined>()
  const [attempting, setAttempting] = useState(false)

  function wrappedOndismiss() {
    setHash(undefined)
    setAttempting(false)
    onDismiss()
  }

  const stakingContract = useRewardsV2Contract(stakingInfo.stakingRewardAddress)
  const serviceFee = useServiceFee(stakingInfo?.stakingRewardAddress, stakingInfo?.boosters?.length) 

  async function onWithdraw() {
    if (stakingContract && stakingInfo?.stakedAmount && serviceFee) {
      const serviceFeeRes = JSBI.BigInt( parseFloat(serviceFee?.toExact()) * Math.pow(10, 18)).toString()

      const estimatedGas = await stakingContract.estimateGas['withdrawBoosters']({ value: serviceFeeRes }).then(estimatedGasLimit => { return estimatedGasLimit })
      const estimatedGasLimit = estimatedGas.mul(120).div(100).toString()
      setAttempting(true)
      await stakingContract
        .withdrawBoosters({value: serviceFeeRes, gasLimit: estimatedGasLimit })
        .then((response: TransactionResponse) => {
          addTransaction(response, {
            summary: `Withdraw booster NFTs`
          })
          setHash(response.hash)
        })
        .catch((error: any) => {
          setAttempting(false)
          console.log(error)
        })
    }
  }

  let error: string | undefined
  if (!account) {
    error = 'Connect Wallet'
  }
  if (!stakingInfo?.stakedAmount) {
    error = error ?? 'Enter an amount'
  }

  return (
    <Modal isOpen={isOpen} onDismiss={wrappedOndismiss} maxHeight={90}>
      {!attempting && !hash && (
        <ContentWrapper gap="lg">
          <RowBetween>
            <TYPE.mediumHeader>Withdraw Boosters and Claim </TYPE.mediumHeader>
            <CloseIcon onClick={wrappedOndismiss} />
          </RowBetween>

          {stakingInfo?.stakedAmount && (
            <AutoColumn justify="center" gap="md">
              <TYPE.body fontWeight={600} fontSize={36}>
                {stakingInfo?.boosters.length}
              </TYPE.body>
              <TYPE.body>Deposited NFTs</TYPE.body>
            </AutoColumn>
          )}

          {stakingInfo?.pendingReward && (
            <AutoColumn justify="center" gap="md">
              <TYPE.body fontWeight={600} fontSize={36} style={{ textAlign: 'center'}}>
                <FormattedCurrencyAmount currencyAmount={stakingInfo?.pendingReward} /> 
                <br></br>
                    {` + `}
                  <br></br>
                <FormattedCurrencyAmount currencyAmount={ stakingInfo?.pendingRewardFromBoost }/>
              </TYPE.body>
              <TYPE.body>Unclaimed {stakingInfo?.rewardToken.symbol}</TYPE.body>
            </AutoColumn>
          )}
          <TYPE.subHeader style={{ textAlign: 'center' }}>
            When you withdraw boosters, your {stakingInfo?.rewardToken.symbol} is claimed and your liquidity will remain in
            mining pool.
          </TYPE.subHeader>
          <ButtonError disabled={!!error} error={!!error && !!stakingInfo?.stakedAmount} onClick={onWithdraw}>
            {error ?? 'Withdraw & Claim'}
          </ButtonError>
        </ContentWrapper>
      )}
      {attempting && !hash && (
        <LoadingView onDismiss={wrappedOndismiss}>
          <AutoColumn gap="12px" justify={'center'}>
            <TYPE.body fontSize={20}>Withdrawing {stakingInfo?.boosters.length} {stakingInfo?.collection.name}</TYPE.body>
            <TYPE.body fontSize={20}>
              Claiming {stakingInfo?.pendingReward?.toSignificant(4)} {stakingInfo?.rewardToken.symbol}
            </TYPE.body>
          </AutoColumn>
        </LoadingView>
      )}
      {hash && (
        <SubmittedView onDismiss={wrappedOndismiss} hash={hash}>
          <AutoColumn gap="12px" justify={'center'}>
            <TYPE.largeHeader>Transaction Submitted</TYPE.largeHeader>
            <TYPE.body fontSize={20}>Withdrew Boosters!</TYPE.body>
            <TYPE.body fontSize={20}>Claimed {stakingInfo?.rewardToken.symbol}!</TYPE.body>
          </AutoColumn>
        </SubmittedView>
      )}
    </Modal>
  )
}
